import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Send, Clock, CheckCircle, XCircle, Users, RefreshCw } from 'lucide-react';
import { BACKEND_URL } from '../../utils/api';

const API = `${BACKEND_URL}/api`;

/**
 * Read-only admin panel for monitoring the multi-stage Spanish drip campaign.
 * Shows: total enrolled leads, sent count, pending, failed, breakdown by stage.
 */
export default function SpanishDripStats({ getAuthHeaders }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchStats = async () => {
    setRefreshing(true);
    try {
      const headers = getAuthHeaders ? getAuthHeaders() : { Authorization: `Bearer ${localStorage.getItem('admin_token')}` };
      const res = await axios.get(`${API}/admin/leads/spanish-drip-stats`, { headers });
      setStats(res.data);
    } catch (e) {
      console.error('Failed to fetch drip stats:', e);
    }
    setLoading(false);
    setRefreshing(false);
  };

  useEffect(() => {
    fetchStats();
    const interval = setInterval(fetchStats, 60_000); // auto-refresh every minute
    return () => clearInterval(interval);
  }, []);

  if (loading) return <div className="text-sm text-slate-400 text-center py-8">Loading drip stats...</div>;
  if (!stats || stats.total === 0) {
    return (
      <Card className="bg-slate-50 border-slate-200">
        <CardContent className="py-8 text-center">
          <Send className="w-8 h-8 text-slate-400 mx-auto mb-3" />
          <p className="text-sm text-slate-600 font-medium">No Spanish drip campaigns active yet</p>
          <p className="text-xs text-slate-400 mt-1">
            Use the "Start ES Drip" button on the leads list to enroll Spanish leads in the 4-stage drip.
          </p>
        </CardContent>
      </Card>
    );
  }

  const { total, sent, pending, failed, enrolled_leads, by_stage, by_variant } = stats;
  const sentPct = total > 0 ? Math.round((sent / total) * 100) : 0;
  const stageMeta = [
    { stage: 1, label: 'Day 0', subtitle: 'Welcome' },
    { stage: 2, label: 'Day 3', subtitle: 'Benefits' },
    { stage: 3, label: 'Day 7', subtitle: 'Carlos testimonial' },
    { stage: 4, label: 'Day 14', subtitle: 'Final CTA' },
  ];

  return (
    <div className="space-y-4" data-testid="spanish-drip-stats-panel">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
            <Send className="w-4 h-4 text-purple-600" /> ES Drip Campaign Stats
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">Auto-refreshes every minute · {enrolled_leads} unique lead{enrolled_leads !== 1 ? 's' : ''} enrolled</p>
        </div>
        <button onClick={fetchStats} disabled={refreshing} className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1" data-testid="drip-refresh-btn">
          <RefreshCw className={`w-3 h-3 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="bg-white border-slate-200">
          <CardContent className="pt-4 pb-4 text-center">
            <Users className="w-4 h-4 text-blue-500 mx-auto mb-1" />
            <p className="text-xs text-slate-500 uppercase">Enrolled</p>
            <p className="text-2xl font-bold text-slate-900 mt-0.5" data-testid="drip-enrolled">{enrolled_leads}</p>
          </CardContent>
        </Card>
        <Card className="bg-white border-slate-200">
          <CardContent className="pt-4 pb-4 text-center">
            <CheckCircle className="w-4 h-4 text-green-500 mx-auto mb-1" />
            <p className="text-xs text-slate-500 uppercase">Sent</p>
            <p className="text-2xl font-bold text-green-600 mt-0.5" data-testid="drip-sent">{sent}</p>
            <p className="text-[10px] text-slate-400 mt-0.5">{sentPct}% of {total}</p>
          </CardContent>
        </Card>
        <Card className="bg-white border-slate-200">
          <CardContent className="pt-4 pb-4 text-center">
            <Clock className="w-4 h-4 text-amber-500 mx-auto mb-1" />
            <p className="text-xs text-slate-500 uppercase">Pending</p>
            <p className="text-2xl font-bold text-amber-600 mt-0.5" data-testid="drip-pending">{pending}</p>
          </CardContent>
        </Card>
        <Card className="bg-white border-slate-200">
          <CardContent className="pt-4 pb-4 text-center">
            <XCircle className="w-4 h-4 text-red-500 mx-auto mb-1" />
            <p className="text-xs text-slate-500 uppercase">Failed</p>
            <p className="text-2xl font-bold text-red-500 mt-0.5" data-testid="drip-failed">{failed}</p>
          </CardContent>
        </Card>
      </div>

      {/* Stage breakdown */}
      <Card className="bg-white border-slate-200">
        <CardContent className="p-4">
          <p className="text-xs font-semibold text-slate-600 uppercase mb-3">Breakdown by Stage</p>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            {stageMeta.map(s => {
              const data = by_stage?.[`stage_${s.stage}`] || { sent: 0, pending: 0 };
              const stageTotal = data.sent + data.pending;
              const pct = stageTotal > 0 ? Math.round((data.sent / stageTotal) * 100) : 0;
              return (
                <div key={s.stage} className="border border-slate-200 rounded-lg p-3" data-testid={`drip-stage-${s.stage}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-semibold text-slate-700">{s.label}</span>
                    <span className="text-[10px] text-slate-400">Stage {s.stage}</span>
                  </div>
                  <p className="text-[11px] text-slate-500 mb-2">{s.subtitle}</p>
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-green-600">Sent</span>
                      <span className="font-semibold text-slate-900">{data.sent}</span>
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-amber-600">Pending</span>
                      <span className="font-semibold text-slate-900">{data.pending}</span>
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-1.5 mt-2">
                      <div className="h-1.5 rounded-full bg-green-500 transition-all" style={{ width: `${pct}%` }} />
                    </div>
                    <p className="text-[10px] text-slate-400 text-center">{pct}% delivered</p>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* A/B Variant Breakdown */}
      {by_variant && (
        <Card className="bg-white border-slate-200">
          <CardContent className="p-4">
            <p className="text-xs font-semibold text-slate-600 uppercase mb-3">A/B Subject Line Variants</p>
            <p className="text-[11px] text-slate-500 mb-3">Each lead is deterministically assigned a variant per stage. Compare delivery counts to gauge which subject performs better once you have ≥30 sends per cell.</p>
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="ab-variants-table">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left text-slate-500 font-medium py-2 pr-4">Stage</th>
                    <th className="text-center text-slate-500 font-medium py-2 px-2">Variant A — Sent</th>
                    <th className="text-center text-slate-500 font-medium py-2 px-2">Variant A — Pending</th>
                    <th className="text-center text-slate-500 font-medium py-2 px-2">Variant B — Sent</th>
                    <th className="text-center text-slate-500 font-medium py-2 px-2">Variant B — Pending</th>
                  </tr>
                </thead>
                <tbody>
                  {stageMeta.map(s => {
                    const a = by_variant[`stage_${s.stage}`]?.A || { sent: 0, pending: 0 };
                    const b = by_variant[`stage_${s.stage}`]?.B || { sent: 0, pending: 0 };
                    return (
                      <tr key={s.stage} className="border-b border-slate-100">
                        <td className="py-2 pr-4">
                          <span className="font-semibold text-slate-700">{s.label}</span>
                          <span className="text-[10px] text-slate-400 ml-1">({s.subtitle})</span>
                        </td>
                        <td className="py-2 px-2 text-center text-green-600 font-semibold">{a.sent}</td>
                        <td className="py-2 px-2 text-center text-amber-600">{a.pending}</td>
                        <td className="py-2 px-2 text-center text-green-600 font-semibold">{b.sent}</td>
                        <td className="py-2 px-2 text-center text-amber-600">{b.pending}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
